import { useIpAddressWhitelistsTableColumns } from '@/features/ip_address_whitelists/components/IpAddressWhitelistsTable/Columns/column';
import type { IpAddressWhitelistInlineFormValues } from '@/features/ip_address_whitelists/IpAddressWhitelistList/types';
import type { IpAddressWhitelistTableRow } from '@/features/ip_address_whitelists/type';
import { useTranslation } from '@/i18n';
import { ButtonV2, Table } from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, memo, useMemo } from 'react';
import type { Control } from 'react-hook-form';
import styles from './IpAddressWhitelistsTable.module.scss';

const cx = classnames.bind(styles);

const STATIC_PAGINATION = {
  maxPage: 1,
  currentPage: 1,
  onChange: () => {},
} as const;

export type IpAddressWhitelistsTableProps = {
  data?: IpAddressWhitelistTableRow[];
  isWhitelistEmpty: boolean;
  isLoading?: boolean;
  isEditingRestrict: boolean;
  isDeleteBlockedByRestriction: boolean;
  isAddModalOpen: boolean;
  editingRowId: string | null;
  inlineFormControl: Control<IpAddressWhitelistInlineFormValues>;
  isInlineSaveDisabled: boolean;
  onSaveInline: () => void;
  onCancelInline: () => void;
  onClickAdd?: () => void;
  onStartEditRow: (row: IpAddressWhitelistTableRow) => void;
  onDeleteRow: (row: IpAddressWhitelistTableRow) => void | Promise<void>;
};

export const IpAddressWhitelistsTable: FC<IpAddressWhitelistsTableProps> = memo(
  ({
    data = [],
    isWhitelistEmpty,
    isLoading = false,
    isEditingRestrict,
    isDeleteBlockedByRestriction,
    isAddModalOpen,
    editingRowId,
    inlineFormControl,
    isInlineSaveDisabled,
    onSaveInline,
    onCancelInline,
    onClickAdd,
    onStartEditRow,
    onDeleteRow,
  }) => {
    const { t } = useTranslation();
    const columns = useIpAddressWhitelistsTableColumns({
      editingRowId,
      inlineFormControl,
      isInlineSaveDisabled,
      isEditingRestrict,
      isDeleteBlockedByRestriction,
      isAddModalOpen,
      onSaveInline,
      onCancelInline,
      onStartEdit: onStartEditRow,
      onDeleteRow,
    });

    const isAddDisabled =
      isEditingRestrict || isAddModalOpen || editingRowId !== null;

    const tableAction = useMemo(
      () =>
        onClickAdd ? (
          <ButtonV2
            size='sm'
            onClick={onClickAdd}
            isDisabled={isAddDisabled}
            testId='ip-address-whitelist-add-new'
          >
            {t('ip_address_whitelist_add_new')}
          </ButtonV2>
        ) : undefined,
      [isAddDisabled, onClickAdd, t]
    );

    const tableData = isWhitelistEmpty ? [] : data;

    return (
      <section className={cx('section')}>
        <div className={cx('heading')}>
          <div className={cx('heading-text')}>
            <h2 className={cx('title')}>
              {t('ip_address_whitelist_table_title')}
            </h2>
          </div>
        </div>
        <div className={cx('table-wrap')}>
          <Table<IpAddressWhitelistTableRow>
            legacy={false}
            rowKey='id'
            columns={columns}
            data={tableData}
            emptyType='object'
            emptyText={t('ip_address_whitelist_table_empty')}
            loading={isLoading}
            pagination={STATIC_PAGINATION}
            hideFooterPagination
            testId='ip-address-whitelists-table'
            tableAction={tableAction}
            verticalAlign='middle'
          />
        </div>
      </section>
    );
  }
);
IpAddressWhitelistsTable.displayName = 'IpAddressWhitelistsTable';
